import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { ScanLine, Send, Truck, UserCheck, ArrowDownToLine, ArrowUpFromLine, Lock, Anchor, Settings2, SearchCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, SIZES, CONDITIONS, MOVEMENT_LABELS } from "../lib/api";
import { OcrScanner } from "../components/OcrScanner";
import { PhotoCapture } from "../components/PhotoCapture";
import { PortsDialog } from "../components/PortsDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Checkbox } from "../components/ui/checkbox";

const PHOTO_SLOTS = [
  { key: "frontal", label: "Foto Frontal" },
  { key: "trasera", label: "Foto Trasera" },
  { key: "lateral_izquierdo", label: "Lateral Izquierdo" },
  { key: "lateral_derecho", label: "Lateral Derecho" },
];

const nowLocal = () => {
  const d = new Date();
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};

export default function NuevoMovimiento() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [ocrOpen, setOcrOpen] = useState(false);
  const [portsOpen, setPortsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    container_number: "", size: "", movement_type: "", operation_type: "Entrada",
    operation_number: "", seal_number: "", container_condition: "Bueno",
    fuera_de_rampa: false, ramp_number: "", arrival_datetime: nowLocal(),
    departure_datetime: "", truck_id: "", driver_id: "",
    process_status: "Arribo y Espera", proceso_completado: false,
    released: "", bl_number: "", puerto: "",
  });
  const [photos, setPhotos] = useState({});

  const { data: trucks = [] } = useQuery({ queryKey: ["trucks"], queryFn: async () => (await api.get("/trucks")).data });
  const { data: drivers = [] } = useQuery({ queryKey: ["drivers"], queryFn: async () => (await api.get("/drivers")).data });
  const { data: containers = [] } = useQuery({ queryKey: ["containers"], queryFn: async () => (await api.get("/containers")).data });
  const { data: ports = [] } = useQuery({ queryKey: ["ports"], queryFn: async () => (await api.get("/ports")).data });

  const PROCESS_STATUSES = ["Arribo y Espera", "En Descarga", "Descargado / Vacío", "Liberado / Despachado"];
  const locked = !!containers.find((c) => c.container_number === form.container_number)?.proceso_completado;

  const loadContainer = (cn) => {
    const item = containers.find((c) => c.container_number === cn);
    if (!item) return;
    setForm((f) => ({
      ...f, container_number: cn,
      size: item.size || f.size,
      container_condition: item.container_condition || f.container_condition,
      process_status: item.process_status || f.process_status,
      puerto: item.puerto || f.puerto,
      released: item.released || f.released,
      bl_number: item.bl_number || f.bl_number,
    }));
    if (item.proceso_completado) toast.error(`${cn} tiene el proceso COMPLETADO y no admite más movimientos`);
    else toast.info(`Datos de ${cn} cargados desde el registro anterior`);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (locked) {
      toast.error("Este contenedor tiene el proceso completado y no admite más movimientos");
      return;
    }
    if (!form.container_number || !form.size || !form.movement_type || !form.operation_number) {
      toast.error("Complete contenedor, tamaño, tipo de movimiento y número de operación");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, photos };
      if (!payload.departure_datetime) payload.departure_datetime = null;
      const { data } = await api.post("/movements", payload);
      toast.success(`Movimiento ${data.movement_id} registrado. Notificación por correo enviada.`, { duration: 5000 });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      qc.invalidateQueries({ queryKey: ["movements"] });
      navigate("/historial");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const movementTypes = [
    { key: "lleno_entrando", icon: ArrowDownToLine, color: "border-emerald-400 bg-emerald-50 text-emerald-800" },
    { key: "vacio_entrando", icon: ArrowDownToLine, color: "border-sky-400 bg-sky-50 text-sky-800" },
    { key: "lleno_saliendo", icon: ArrowUpFromLine, color: "border-amber-400 bg-amber-50 text-amber-800" },
    { key: "vacio_saliendo", icon: ArrowUpFromLine, color: "border-violet-400 bg-violet-50 text-violet-800" },
  ];

  return (
    <form onSubmit={submit} className="space-y-6 max-w-3xl mx-auto" data-testid="nuevo-movimiento-page">
      <div>
        <h1 className="font-outfit text-2xl md:text-3xl font-extrabold tracking-tight">Registro de Movimiento</h1>
        <p className="text-sm text-slate-500">Se generará un ID único y se notificará por correo automáticamente</p>
      </div>

      <Card className="border-slate-200" data-testid="container-id-card">
        <CardHeader className="pb-3"><CardTitle className="font-outfit text-lg">Identificación del Contenedor</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <div className="flex-1">
              <Label htmlFor="container_number">Número de Contenedor (ISO 6346)</Label>
              <Input id="container_number" value={form.container_number} required maxLength={11}
                     onChange={(e) => set("container_number", e.target.value.toUpperCase())}
                     placeholder="MSCU1234567" data-testid="container-number-input"
                     className="font-mono font-bold tracking-widest text-lg h-12 uppercase" />
            </div>
            <div className="flex items-end">
              <Button type="button" onClick={() => setOcrOpen(true)} data-testid="ocr-open-button"
                      className="h-12 bg-navy hover:bg-navy-800 text-white">
                <ScanLine className="w-4 h-4 mr-2" /> Escanear
              </Button>
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="flex items-center gap-1.5"><SearchCheck className="w-3.5 h-3.5 text-cyan-600" /> Consultar contenedor registrado</Label>
            <Select value="" onValueChange={loadContainer}>
              <SelectTrigger data-testid="container-lookup-select" className="h-11">
                <SelectValue placeholder="Seleccione un contenedor para reutilizar sus datos" />
              </SelectTrigger>
              <SelectContent>
                {containers.length === 0 && <SelectItem value="__none__" disabled data-testid="container-lookup-empty">Sin registros previos</SelectItem>}
                {containers.map((c) => (
                  <SelectItem key={c.container_number} value={c.container_number} data-testid={`container-lookup-${c.container_number}`}>
                    <span className="font-mono">{c.container_number}</span>
                    {c.proceso_completado ? " · COMPLETADO" : ` · ${c.process_status || ""}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {locked && (
            <div className="flex items-center gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2.5 text-sm text-red-700 font-medium" data-testid="container-locked-banner">
              <Lock className="w-4 h-4" /> Proceso Completado: este contenedor no admite más movimientos ni actualizaciones.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {PHOTO_SLOTS.map((slot) => (
              <PhotoCapture key={slot.key} label={slot.label} value={photos[slot.key]}
                            onChange={(v) => setPhotos((p) => ({ ...p, [slot.key]: v }))}
                            testid={`photo-${slot.key}`} />
            ))}
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="movement-details-card">
        <CardHeader className="pb-3"><CardTitle className="font-outfit text-lg">Detalles del Movimiento</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tamaño</Label>
              <Select value={form.size} onValueChange={(v) => set("size", v)}>
                <SelectTrigger data-testid="size-select" className="h-11"><SelectValue placeholder="Seleccione tamaño" /></SelectTrigger>
                <SelectContent>
                  {SIZES.map((s) => <SelectItem key={s} value={s} data-testid={`size-option-${s.replace(/\s/g, "-")}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Estado actual del contenedor</Label>
              <Select value={form.container_condition} onValueChange={(v) => set("container_condition", v)}>
                <SelectTrigger data-testid="condition-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CONDITIONS.map((c) => <SelectItem key={c} value={c} data-testid={`condition-option-${c.replace(/\s/g, "-")}`}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Estatus del proceso</Label>
              <Select value={form.process_status} onValueChange={(v) => set("process_status", v)}>
                <SelectTrigger data-testid="process-status-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PROCESS_STATUSES.map((s) => <SelectItem key={s} value={s} data-testid={`process-status-${s.replace(/[\s/]/g, "-")}`}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Anchor className="w-3.5 h-3.5 text-cyan-600" /> Puerto</Label>
              <div className="flex gap-2">
                <Select value={form.puerto} onValueChange={(v) => set("puerto", v)}>
                  <SelectTrigger data-testid="puerto-select" className="h-11 flex-1"><SelectValue placeholder="Seleccione puerto" /></SelectTrigger>
                  <SelectContent>
                    {ports.map((p) => <SelectItem key={p.id} value={p.nombre} data-testid={`puerto-option-${p.nombre}`}>{p.nombre}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="button" variant="outline" onClick={() => setPortsOpen(true)} data-testid="manage-ports-button"
                        className="h-11 border-navy text-navy shrink-0" title="Gestionar puertos">
                  <Settings2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>

          <label className={`flex items-center gap-2.5 rounded-lg border-2 px-3 py-3 cursor-pointer transition-colors ${
            form.proceso_completado ? "border-slate-700 bg-slate-100" : "border-slate-200 hover:border-slate-300"}`}
            data-testid="proceso-completado-row">
            <Checkbox checked={form.proceso_completado} onCheckedChange={(v) => set("proceso_completado", !!v)} data-testid="proceso-completado-checkbox" />
            <div>
              <span className="text-sm font-bold text-slate-800 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Proceso Completado</span>
              <span className="text-xs text-slate-500">Al marcarlo, este número de contenedor no podrá editarse ni recibir más movimientos.</span>
            </div>
          </label>

          <div className="space-y-2">
            <Label>Tipo de movimiento</Label>
            <div className="grid grid-cols-2 gap-2" data-testid="movement-type-group">
              {movementTypes.map(({ key, icon: Icon, color }) => (
                <button type="button" key={key} onClick={() => set("movement_type", key)}
                        data-testid={`movement-type-${key}`}
                        className={`flex items-center gap-2 rounded-lg border-2 px-3 py-3 text-sm font-semibold transition-all ${
                          form.movement_type === key ? color + " ring-2 ring-offset-1 ring-cyan-500" : "border-slate-200 text-slate-500 hover:border-slate-300"}`}>
                  <Icon className="w-4 h-4" /> {MOVEMENT_LABELS[key]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Tipo de operación</Label>
              <Select value={form.operation_type} onValueChange={(v) => set("operation_type", v)}>
                <SelectTrigger data-testid="operation-type-select" className="h-11"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Entrada", "Salida", "Traspaso"].map((o) => <SelectItem key={o} value={o} data-testid={`operation-option-${o}`}>{o}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="operation_number">Número asignado</Label>
              <Input id="operation_number" value={form.operation_number} required
                     onChange={(e) => set("operation_number", e.target.value)}
                     placeholder="Ej. 1042" className="h-11" data-testid="operation-number-input" />
            </div>
          </div>
          {form.operation_number && (
            <p className="text-sm font-mono bg-slate-900 text-telemetry rounded-md px-3 py-2 inline-block" data-testid="operation-preview">
              {form.operation_type} #{form.operation_number}
            </p>
          )}

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="released">Released</Label>
              <Input id="released" value={form.released} onChange={(e) => set("released", e.target.value)}
                     placeholder="Ej. REL-2026-001" className="h-11" data-testid="released-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="bl_number">Bill of Lading (B/L)</Label>
              <Input id="bl_number" value={form.bl_number} onChange={(e) => set("bl_number", e.target.value)}
                     placeholder="Ej. MSCUBL123456" className="h-11 font-mono" data-testid="bl-number-input" />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="seal_number">Número de sello</Label>
              <Input id="seal_number" value={form.seal_number} onChange={(e) => set("seal_number", e.target.value)}
                     placeholder="Ej. SL-998877" className="h-11 font-mono" data-testid="seal-number-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ramp_number">Número de rampa</Label>
              <Input id="ramp_number" value={form.ramp_number} disabled={form.fuera_de_rampa}
                     onChange={(e) => set("ramp_number", e.target.value)}
                     placeholder="Ej. 7" className="h-11" data-testid="ramp-number-input" />
              <label className="flex items-center gap-2 pt-1 cursor-pointer">
                <Checkbox checked={form.fuera_de_rampa} onCheckedChange={(v) => set("fuera_de_rampa", !!v)} data-testid="off-ramp-checkbox" />
                <span className="text-sm text-slate-600">Fuera de rampa</span>
              </label>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="arrival_datetime">Fecha y hora de llegada</Label>
              <Input id="arrival_datetime" type="datetime-local" value={form.arrival_datetime}
                     onChange={(e) => set("arrival_datetime", e.target.value)} className="h-11" data-testid="arrival-datetime-input" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="departure_datetime">Fecha y hora de salida</Label>
              <Input id="departure_datetime" type="datetime-local" value={form.departure_datetime}
                     onChange={(e) => set("departure_datetime", e.target.value)} className="h-11" data-testid="departure-datetime-input" />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200" data-testid="truck-driver-card">
        <CardHeader className="pb-3"><CardTitle className="font-outfit text-lg flex items-center gap-2"><Truck className="w-4 h-4" /> Camión y Conductor</CardTitle></CardHeader>
        <CardContent className="grid md:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label>Camión (matrícula)</Label>
            <Select value={form.truck_id} onValueChange={(v) => set("truck_id", v)}>
              <SelectTrigger data-testid="truck-select" className="h-11"><SelectValue placeholder="Seleccione camión" /></SelectTrigger>
              <SelectContent>
                {trucks.map((t) => (
                  <SelectItem key={t.id} value={t.id} data-testid={`truck-option-${t.matricula}`}>
                    {t.matricula}{t.es_traspaso ? " (Traspaso)" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/camiones" className="text-xs text-cyan-700 hover:underline" data-testid="goto-trucks-link">+ Registrar nuevo camión</Link>
          </div>
          <div className="space-y-1.5">
            <Label>Conductor</Label>
            <Select value={form.driver_id} onValueChange={(v) => set("driver_id", v)}>
              <SelectTrigger data-testid="driver-select" className="h-11"><SelectValue placeholder="Seleccione conductor" /></SelectTrigger>
              <SelectContent>
                {drivers.map((d) => (
                  <SelectItem key={d.id} value={d.id} data-testid={`driver-option-${d.cedula}`}>{d.nombre} · {d.cedula}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Link to="/conductores" className="text-xs text-cyan-700 hover:underline flex items-center gap-1" data-testid="goto-drivers-link">
              <UserCheck className="w-3 h-3" /> + Registrar nuevo conductor
            </Link>
          </div>
        </CardContent>
      </Card>

      <div className="sticky bottom-20 md:bottom-4 z-10">
        <Button type="submit" disabled={saving || locked} data-testid="submit-movement-button"
                className="w-full h-13 h-14 bg-navy hover:bg-navy-800 text-white font-outfit font-bold text-lg rounded-xl shadow-lg shadow-navy/25">
          <Send className="w-5 h-5 mr-2" /> {saving ? "Registrando..." : "Registrar Movimiento y Notificar"}
        </Button>
      </div>

      <OcrScanner open={ocrOpen} onClose={() => setOcrOpen(false)}
                  onResult={(code) => { set("container_number", code); toast.success(`Código detectado: ${code}`); }} />
      <PortsDialog open={portsOpen} onClose={() => setPortsOpen(false)} />
    </form>
  );
}
